import React from 'react';
import {
  Box,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import LinkMenuIcon from '@/public/icons/link_menu.svg';
import { LoginInfoBoxSx } from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/PayStub/style';
import { DataListGrid, DataListGridItem } from '@/components/DataListGrid';
import { SubContentProps } from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/LanguageSetting';
import ContentDescription, {
  ContentDivider,
} from '@/components/MenuDrawer/MyMenuDrawer/@components/ContentDescription';
import Button from '@mui/material/Button';
import Link from '@/components/@IntranetLibrary/Link';
import ProfileCardPopper from '@/components/@IntranetLibrary/ProfileCard/ProfileCardPopper';
import usePopper from '@/hooks/usePopper';
import { isDevApi } from '@/constant';
import { useMypageEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { MypageEventName } from '@/constant/gtm';
import { useTranslation } from 'react-i18next';

export const PayStubContent = ({ myInfo }: SubContentProps) => {
  const { t } = useTranslation();
  const { email, employeeNumber, phoneMobile } = myInfo;
  const mypageEventTracker = useMypageEventTracker();

  const { anchorEl, openPopper, closePopper } = usePopper<HTMLElement>();

  const CompanyCodeList = [
    { name: 'F&F', code: 'Q623' },
    { name: `F&F ${t('MYPAGE.홀딩스')}`, code: 'Q526' },
    { name: `F&F ${t('MYPAGE.로지스틱스')}`, code: 'Q739' },
    { name: `F&F ${t('MYPAGE.파트너스')}`, code: 'Q597' },
    { name: `F&F ${t('MYPAGE.엔터테인먼트')}`, code: 'Q734' },
  ];

  // 인사총무팀 박미정 대리(6549ee8546c21937d00af645) 고정 (2025년 승진!!)
  const managerName = isDevApi ? 'Dev 박민지 담당' : '인사총무팀 박미정 담당';
  const managerPassportId = isDevApi
    ? '6549f436794d98e9b2029ba4'
    : '6549ee8546c21937d00af645';

  return (
    <ContentDivider>
      <ContentDescription hasVerticalMargin={true}>
        {t('MYPAGE.급여명세서와 연말정산을 조회할 수 있습니다.')}
        <br />
        {t('MYPAGE.법인마다 사용 서비스가 다르니 아래 내용을 확인해 주세요.')}
      </ContentDescription>
      <Stack component={'section'}>
        <Box sx={LoginInfoBoxSx}>
          <h1>iPayView {t('MYPAGE.로그인 정보')}</h1>
          <Table className={'companyCode'}>
            <TableHead>
              <TableRow>
                <TableCell>{t('MYPAGE.대상 회사명')}</TableCell>
                <TableCell align="right">{t('MYPAGE.코드')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {CompanyCodeList.map((company, index) => (
                <TableRow key={`company-${index}`}>
                  <TableCell>{company.name}</TableCell>
                  <TableCell align="right">{company.code}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <DataListGrid
            rowGap={'8px'}
            columnGap={'12px'}
            gridTemplateColumns={'80px 1fr'}
            sx={{ marginTop: '24px' }}
          >
            <DataListGridItem label={t('사번')}>
              {employeeNumber || t('MYPAGE.포탈 마이페이지에서 확인')}
            </DataListGridItem>
            <DataListGridItem label={t('휴대폰')}>
              {phoneMobile || '-'}
            </DataListGridItem>
            <DataListGridItem label={t('비밀번호')}>
              {t('MYPAGE.iPayView에서 설정한 비밀번호')}
            </DataListGridItem>
          </DataListGrid>
        </Box>
        <Link
          href={'https://www.ipayview.com/'}
          target={'_blank'}
          size={'large'}
          rel={'noreferrer'}
          variant={'primary'}
          sx={{ margin: '24px 0 32px' }}
          onClick={() => {
            // GA Mypage - 28번
            mypageEventTracker(MypageEventName.clickOutLink, {
              area_name: '급여명세서 조회',
              button_text: 'iPayView 열기',
            });
          }}
        >
          <SvgIcon
            sx={{
              width: '20px',
              height: '20px',
              marginRight: '4px',
              path: { stroke: 'currentColor' },
            }}
          >
            <LinkMenuIcon />
          </SvgIcon>
          iPayView {t('열기')}
        </Link>
      </Stack>
      <Stack component={'section'}>
        <Box sx={LoginInfoBoxSx}>
          <h1>F&CO · WORKUP {t('MYPAGE.로그인 정보')}</h1>
          <DataListGrid
            rowGap={'8px'}
            columnGap={'12px'}
            gridTemplateColumns={'80px 1fr'}
            sx={{ marginTop: '16px' }}
          >
            <DataListGridItem label={t('아이디')}>{email}</DataListGridItem>
            <DataListGridItem label={t('비밀번호')}>
              {t('MYPAGE.Workup에서 설정한 비밀번호')}
            </DataListGridItem>
            <DataListGridItem label={t('문의')}>
              <Button
                onClick={(event) => {
                  openPopper(event);

                  // GA Mypage - 29번
                  mypageEventTracker(MypageEventName.viewProfile, {
                    area_name: '급여명세서 조회',
                    employees: managerName,
                  });
                }}
                sx={{
                  padding: 0,
                  textDecoration: 'underline !important',
                  backgroundColor: 'var(--dk-bg, white) !important',
                  lineHeight: '1.5',
                  fontSize: '14px',
                  color: 'var(--dk-accent, #06C)',
                }}
              >
                {t(`MYPAGE.${managerName}`)}
              </Button>
            </DataListGridItem>
          </DataListGrid>
        </Box>
        <Link
          href={'https://hr.workup.plus/cloudhr/1.0/login'}
          target={'_blank'}
          rel={'noreferrer'}
          size={'large'}
          variant={'secondary'}
          sx={{ margin: '24px 0 32px' }}
          onClick={() => {
            // GA Mypage - 30번
            mypageEventTracker(MypageEventName.clickOutLink, {
              area_name: '급여명세서 조회',
              button_text: 'WORKUP 열기',
            });
          }}
        >
          <SvgIcon
            sx={{
              width: '20px',
              height: '20px',
              marginRight: '4px',
              path: { stroke: 'var(--dk-text, black)' },
            }}
          >
            <LinkMenuIcon />
          </SvgIcon>
          WORKUP {t('열기')}
        </Link>
        <ProfileCardPopper
          passportId={managerPassportId}
          anchorEl={anchorEl}
          onClose={closePopper}
        />
      </Stack>
    </ContentDivider>
  );
};
